import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TrackingContent } from "../types";
import ProgressStatusBadge from "./ProgressStatusBadge";
import ScoStatusBadge from "./ScoStatusBadge";

interface TrackingContentPanelProps {
  contents: TrackingContent[];
  isLoading: boolean;
  hasError: boolean;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onClose: () => void;
}

const TrackingContentPanel: React.FC<TrackingContentPanelProps> = ({
  contents,
  isLoading,
  hasError,
  selectedIndex,
  onSelect,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const messageSx = { fontSize: "14px", color: theme.palette.text.primary };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        padding: "12px 14px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        minHeight: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          {t("tracking.panel-title")}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          size="small"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : hasError ? (
        <Typography sx={messageSx}>{t("tracking.error")}</Typography>
      ) : contents.length === 0 ? (
        <Typography sx={messageSx}>{t("tracking.empty")}</Typography>
      ) : (
        contents.map((content) => {
          const isSelected = content.index === selectedIndex;
          return (
            <Box
              key={content.index}
              onClick={() => onSelect(content.index)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "16px 20px",
                borderRadius: "12px",
                cursor: "pointer",
                border: `1px solid ${
                  isSelected ? theme.palette.text.primary : "transparent"
                }`,
                backgroundColor: theme.palette.background.card,
              }}
            >
              <Radio
                checked={isSelected}
                onChange={() => onSelect(content.index)}
                sx={{
                  color: theme.palette.divider,
                  "&.Mui-checked": { color: theme.palette.primary.main },
                  padding: "4px",
                }}
              />
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                  {content.scoId ??
                    (content.isMulti !== true
                      ? t("tracking.content-single")
                      : t("tracking.content-label", {
                          index: content.index + 1,
                        }))}
                </Typography>
                <Typography sx={{ fontSize: "12px", lineHeight: 1.4 }}>
                  {t(`tracking.kind.${content.kind}`)}
                </Typography>
              </Box>
              {content.kind === "scorm" ? (
                <ScoStatusBadge status={content.status} />
              ) : (
                <ProgressStatusBadge source={{ status: content.status }} />
              )}
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default TrackingContentPanel;
